import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  Box, Button, Collapse, Divider, Flex, FormControl, FormHelperText, FormLabel, HStack, Input, InputGroup, InputRightAddon,
  Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Select, SimpleGrid, Text,
  Textarea, VStack, useToast, Switch,
} from '@chakra-ui/react'
import { Calculator } from 'lucide-react'
import { operatorsAdmin, type CommissionRule } from '@services/operators'
import { cmsService } from '@services/api'
import { apiErrorMessage, formatNumber, formatTzs } from '@utils/format'
import { Label } from '@components/ui'

/** Mirrors the server's rule maths so the admin sees the outcome while typing. The server stays the source of truth. */
export function previewCommission(fare: number, pct: number, fixed: number, min: number | null, max: number | null) {
  let cents = Math.round((fare * 100 * Math.round(pct * 100)) / 10000) + Math.round(fixed * 100)
  if (min != null) cents = Math.max(cents, Math.round(min * 100))
  if (max != null) cents = Math.min(cents, Math.round(max * 100))
  return Math.min(Math.max(Math.round(cents / 100), 0), Math.round(fare))
}

const toLocalInput = (iso?: string | null) => (iso ? new Date(iso).toISOString().slice(0, 16) : '')

export default function CommissionRuleModal({
  isOpen, onClose, rule, defaultOperatorId, lockOperator,
}: { isOpen: boolean; onClose: () => void; rule?: CommissionRule | null; defaultOperatorId?: string; lockOperator?: boolean }) {
  const qc = useQueryClient()
  const toast = useToast()
  const editing = !!rule

  const [name, setName] = useState('')
  const [operatorId, setOperatorId] = useState('')
  const [routeId, setRouteId] = useState('')
  const [daladalaId, setDaladalaId] = useState('')
  const [percentage, setPercentage] = useState('10')
  const [fixedAmount, setFixedAmount] = useState('')
  const [minAmount, setMinAmount] = useState('')
  const [maxAmount, setMaxAmount] = useState('')
  const [priority, setPriority] = useState('0')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [effectiveTo, setEffectiveTo] = useState('')
  const [notes, setNotes] = useState('')
  const [active, setActive] = useState(true)
  const [advanced, setAdvanced] = useState(false)
  const [fare, setFare] = useState('6000')

  useEffect(() => {
    if (!isOpen) return
    setName(rule?.name ?? ''); setOperatorId(rule?.operator?.id ?? defaultOperatorId ?? ''); setRouteId(rule?.routeId ?? ''); setDaladalaId(rule?.daladalaId ?? '')
    setPercentage(String(rule?.percentage ?? 10)); setFixedAmount(rule?.fixedAmount ? String(rule.fixedAmount) : '')
    setMinAmount(rule?.minAmount != null ? String(rule.minAmount) : ''); setMaxAmount(rule?.maxAmount != null ? String(rule.maxAmount) : '')
    setPriority(String(rule?.priority ?? 0)); setEffectiveFrom(toLocalInput(rule?.effectiveFrom)); setEffectiveTo(toLocalInput(rule?.effectiveTo))
    setNotes(rule?.notes ?? ''); setActive(rule?.active ?? true); setAdvanced(!!(rule && (rule.minAmount != null || rule.maxAmount != null || rule.priority || rule.effectiveTo)))
  }, [isOpen, rule, defaultOperatorId])

  const { data: operators } = useQuery('rule-operators', () => operatorsAdmin.list({ limit: 100 }), { enabled: isOpen && !lockOperator, staleTime: 60_000 })
  const { data: routes } = useQuery('rule-routes', () => cmsService.getRoutes({ limit: 200 } as any), { enabled: isOpen, staleTime: 120_000 })
  const { data: detail } = useQuery(['rule-fleet', operatorId], () => operatorsAdmin.detail(operatorId).then(r => r.data), { enabled: isOpen && !!operatorId, staleTime: 60_000 })

  const scope = useMemo(() => {
    if (daladalaId) return 'One bus'
    if (operatorId && routeId) return 'One company on one route'
    if (operatorId) return 'One company'
    if (routeId) return 'One route, all companies'
    return 'Everyone (platform default)'
  }, [operatorId, routeId, daladalaId])

  const pct = Number(percentage) || 0
  const fx = Number(fixedAmount) || 0
  const mn = minAmount === '' ? null : Number(minAmount)
  const mx = maxAmount === '' ? null : Number(maxAmount)
  const fareNum = Number(fare) || 0
  const commission = previewCommission(fareNum, pct, fx, mn, mx)

  const body = () => ({
    name: name.trim() || scope, operatorId: operatorId || null, routeId: routeId || null, daladalaId: daladalaId || null,
    percentage: pct, fixedAmount: fx, minAmount: mn, maxAmount: mx, priority: Number(priority) || 0, active,
    effectiveFrom: effectiveFrom ? new Date(effectiveFrom).toISOString() : undefined,
    effectiveTo: effectiveTo ? new Date(effectiveTo).toISOString() : null, notes: notes || null,
  })
  const save = useMutation(() => (editing ? operatorsAdmin.updateRule(rule!.id, body()) : operatorsAdmin.createRule(body())), {
    onSuccess: () => {
      toast({ title: editing ? 'Commission updated' : 'Commission rule created', status: 'success' })
      qc.invalidateQueries('commission-rules'); qc.invalidateQueries('operator-detail'); onClose()
    },
    onError: e => { toast({ title: apiErrorMessage(e), status: 'error' }) },
  })
  const invalid = pct < 0 || pct > 100 || fx < 0 || (mn != null && mx != null && mn > mx)

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(6px)" />
      <ModalContent borderRadius="2xl">
        <ModalHeader>{editing ? 'Edit commission rule' : 'New commission rule'}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            <Box p={3} bg="gray.50" borderRadius="xl"><Label>Applies to</Label><Text fontWeight="semibold" mt={0.5}>{scope}</Text>
              <Text fontSize="xs" color="gray.500">The most specific matching rule wins: bus, then company + route, then company, then route, then everyone.</Text></Box>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {!lockOperator && (
                <FormControl><FormLabel fontSize="sm">Company</FormLabel>
                  <Select value={operatorId} onChange={e => { setOperatorId(e.target.value); setDaladalaId('') }}>
                    <option value="">All companies</option>
                    {(operators?.data ?? []).filter(o => !o.isHouse).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                  </Select></FormControl>
              )}
              <FormControl><FormLabel fontSize="sm">Route</FormLabel>
                <Select value={routeId} onChange={e => setRouteId(e.target.value)}>
                  <option value="">All routes</option>
                  {((routes as any)?.data ?? []).map((r: any) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select></FormControl>
              {operatorId && (
                <FormControl><FormLabel fontSize="sm">Bus</FormLabel>
                  <Select value={daladalaId} onChange={e => setDaladalaId(e.target.value)}>
                    <option value="">All of the company's buses</option>
                    {(detail?.fleet ?? []).map(b => <option key={b.id} value={b.id}>{b.registrationNumber}</option>)}
                  </Select></FormControl>
              )}
            </SimpleGrid>

            <Divider />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <FormControl isInvalid={pct < 0 || pct > 100}><FormLabel fontSize="sm">Percentage of each fare</FormLabel>
                <InputGroup><Input type="number" min={0} max={100} step="0.01" value={percentage} onChange={e => setPercentage(e.target.value)} /><InputRightAddon>%</InputRightAddon></InputGroup></FormControl>
              <FormControl><FormLabel fontSize="sm">Plus a fixed amount per ride</FormLabel>
                <InputGroup><Input type="number" min={0} value={fixedAmount} placeholder="0" onChange={e => setFixedAmount(e.target.value)} /><InputRightAddon>TZS</InputRightAddon></InputGroup></FormControl>
            </SimpleGrid>

            <Flex align="center" justify="space-between"><Text fontSize="sm" color="gray.600">Caps, schedule and priority</Text><Switch isChecked={advanced} onChange={e => setAdvanced(e.target.checked)} colorScheme="green" /></Flex>
            <Collapse in={advanced} animateOpacity>
              <VStack spacing={4} align="stretch" pt={1}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl><FormLabel fontSize="sm">Never less than</FormLabel><InputGroup><Input type="number" min={0} value={minAmount} onChange={e => setMinAmount(e.target.value)} /><InputRightAddon>TZS</InputRightAddon></InputGroup></FormControl>
                  <FormControl><FormLabel fontSize="sm">Never more than</FormLabel><InputGroup><Input type="number" min={0} value={maxAmount} onChange={e => setMaxAmount(e.target.value)} /><InputRightAddon>TZS</InputRightAddon></InputGroup></FormControl>
                  <FormControl><FormLabel fontSize="sm">Starts</FormLabel><Input type="datetime-local" value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} />
                    <FormHelperText>Leave empty to start now. Schedule a rate change by starting a new rule when the old one ends.</FormHelperText></FormControl>
                  <FormControl><FormLabel fontSize="sm">Ends</FormLabel><Input type="datetime-local" value={effectiveTo} onChange={e => setEffectiveTo(e.target.value)} /></FormControl>
                  <FormControl><FormLabel fontSize="sm">Priority</FormLabel><Input type="number" value={priority} onChange={e => setPriority(e.target.value)} />
                    <FormHelperText>Breaks ties between rules of the same specificity.</FormHelperText></FormControl>
                  <FormControl><FormLabel fontSize="sm">Rule name</FormLabel><Input value={name} onChange={e => setName(e.target.value)} placeholder={scope} /></FormControl>
                </SimpleGrid>
                <FormControl><FormLabel fontSize="sm">Internal notes</FormLabel><Textarea rows={2} value={notes} onChange={e => setNotes(e.target.value)} /></FormControl>
              </VStack>
            </Collapse>

            <Box p={4} borderRadius="xl" bgGradient="linear(to-br, brand.50, white)" border="1px solid" borderColor="brand.100">
              <HStack justify="space-between" mb={3}><HStack spacing={2} color="brand.700"><Calculator size={16} /><Text fontSize="sm" fontWeight="semibold">Try it on a ride</Text></HStack>
                <InputGroup size="sm" w="150px"><Input type="number" value={fare} onChange={e => setFare(e.target.value)} bg="white" /><InputRightAddon>TZS</InputRightAddon></InputGroup></HStack>
              <SimpleGrid columns={2} spacing={4}>
                <Box><Label>Daladala Smart keeps</Label><Text fontSize="xl" fontWeight="semibold">{formatTzs(commission)}</Text>
                  <Text fontSize="xs" color="gray.500">{fareNum ? ((commission / fareNum) * 100).toFixed(1) : '0'}% of the fare</Text></Box>
                <Box><Label>Company earns</Label><Text fontSize="xl" fontWeight="semibold" color="brand.700">{formatTzs(fareNum - commission)}</Text>
                  <Text fontSize="xs" color="gray.500">before any LATRA levy pass-through</Text></Box>
              </SimpleGrid>
            </Box>

            {editing && <Flex align="center" justify="space-between"><Text fontSize="sm">Rule is active</Text><Switch isChecked={active} onChange={e => setActive(e.target.checked)} colorScheme="green" /></Flex>}
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button isDisabled={invalid} isLoading={save.isLoading} onClick={() => save.mutate()}>{editing ? 'Save changes' : 'Create rule'}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export { formatNumber }
